/**
 * Firebase SDK 初期化。
 * 環境変数は VITE_FIREBASE_* prefix で .env / .env.local に定義する。
 * 本番デプロイ時は CI で .env.production を生成してビルドする想定。
 */
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

import { FUNCTIONS_REGION } from '@goenhub/shared';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  // eslint-disable-next-line no-console
  console.warn(
    '[firebase] 環境変数が未設定です。admin-web/.env または .env.local で VITE_FIREBASE_* を定義してください。',
  );
}

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

const useEmulator =
  import.meta.env.VITE_USE_EMULATORS === 'true' && import.meta.env.DEV;

/**
 * Firestore 初期化。
 * - `ignoreUndefinedProperties`: undefined プロパティを書込時に自動除外。
 * - `localCache` (本番のみ): IndexedDB ベースの永続キャッシュを有効化。
 *   2回目以降のアクセスはキャッシュ返却 + 差分のみネットワーク取得となり、
 *   `getDocs` の読み取り回数が激減する（複数タブ同期も対応）。
 *   エミュレータ時は無効化（開発中のリセット混乱を避ける）。
 */
export const db = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  ...(useEmulator
    ? {}
    : {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      }),
});
export const storage = getStorage(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);

if (useEmulator) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  // eslint-disable-next-line no-console
  console.info('[firebase] Emulator 接続モード');
}
